import React, { useEffect, useState, useRef } from 'react';
import ParameterIcon from './ParameterIcon.jsx';
import BonusModal from './BonusModal.jsx';
import getColor from '../utils/interpolateColor';
import { getProgressBarFillPercent } from '../utils/progressBarDisplayWidth';

const BarBackground = ({
  score,
  color,
  hasIcon,
  children,
  onMouseOver,
  onMouseOut,
  onTouchStart,
  innerRef,
}) => (
  <div
    ref={innerRef}
    className="w-full rounded-[30px] flex justify-between items-center relative"
    style={{
      backgroundColor: score && score === 100 ? color : '#e8eaea',
      padding: hasIcon ? '0 12px 0 0' : '7px 12px',
      marginBottom: 6,
    }}
    onMouseOver={onMouseOver}
    onMouseOut={onMouseOut}
    onTouchStart={onTouchStart}
  >
    {children}
  </div>
);

const ProgressBarComponent = ({ name, score, bonus = null, isIcon = false, t }) => {
  const [translatedTitle, setTranslatedTitle] = useState('');
  const [showBonus, setShowBonus] = useState(false);
  const [bonusScore, setBonusScore] = useState(null);
  const [totalScore, setTotalScore] = useState(0);
  const [scorePlaceHolder, setScorePlaceHolder] = useState(null);
  const progressBarRef = useRef(null);

  useEffect(() => {
    if (bonus !== null && bonus !== undefined) {
      const val = typeof bonus === 'object' && bonus?.value != null ? bonus.value : bonus;
      setBonusScore(Number(val) > 0 ? Number(val) : null);
    } else {
      setBonusScore(null);
    }
  }, [bonus]);

  useEffect(() => {
    if (name && typeof name === 'string' && name.trim() !== '') {
      setTranslatedTitle(t ? t(name) : name);
    } else {
      setTranslatedTitle('');
    }
  }, [name, t]);

  useEffect(() => {
    const total = (Number(score) || 0) + (bonusScore || 0);
    setTotalScore(Math.min(total, 100));
  }, [score, bonusScore]);

  useEffect(() => {
    if (score == null || score === '') {
      setScorePlaceHolder('●');
    } else {
      setScorePlaceHolder(Math.round(totalScore));
    }
  }, [score, totalScore]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (progressBarRef.current && !progressBarRef.current.contains(event.target)) {
        setShowBonus(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const barColor = getColor(totalScore);
  // Core compare: green bonus bar = total width; coloured bar on top = base score only
  // so the green “stick-out” is the bonus segment.
  const bonusBarWidth =
    bonusScore > 0 && score != null && score !== ''
      ? getProgressBarFillPercent(totalScore)
      : 0;
  const foregroundWidth =
    score != null && score !== '' ? getProgressBarFillPercent(Number(score) || 0) : 0;

  const handleBonusClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div className="relative w-full pb-[5px]">
      <BarBackground
        innerRef={progressBarRef}
        score={score}
        color={barColor}
        hasIcon={isIcon}
        onMouseOver={() => setShowBonus(true)}
        onTouchStart={() => setShowBonus(true)}
        onMouseOut={() => setShowBonus(false)}
      >
        <div className="flex items-center z-[3]" style={{ marginLeft: 0 }}>
          {isIcon && (
            <ParameterIcon title={name} width={44} height={44} />
          )}
          <span style={{ fontSize: '1rem', marginLeft: 10, whiteSpace: 'nowrap', overflow: 'visible' }}>
            {translatedTitle}
          </span>
        </div>
        {bonusBarWidth > 0 && (
          <div
            className="absolute top-0 left-0 bottom-0 rounded-[30px] z-[1]"
            style={{
              width: `${bonusBarWidth}%`,
              backgroundColor: '#c6edd0',
            }}
            aria-hidden
          />
        )}
        {foregroundWidth > 0 && (
          <div
            className="absolute top-0 left-0 bottom-0 rounded-[30px] z-[2]"
            style={{
              width: `${foregroundWidth}%`,
              backgroundColor: barColor,
            }}
          />
        )}
        <span
          className="float-right font-bold z-[3]"
          style={{ color: score != null ? '#000' : '#595959', fontSize: '0.9rem' }}
        >
          {scorePlaceHolder}/100
        </span>
        {bonusScore != null && bonusScore > 0 && showBonus && (
          <BonusModal
            name={translatedTitle}
            points={bonusScore}
            certificate={typeof bonus === 'object' ? bonus?.certificate : null}
            totalScore={totalScore}
            imgFile={typeof bonus === 'object' ? bonus?.imgFile : null}
            imgFiles={typeof bonus === 'object' ? bonus?.imgFiles : []}
            t={t || ((k) => k)}
            onClick={handleBonusClick}
          />
        )}
      </BarBackground>
    </div>
  );
};

export default ProgressBarComponent;
